import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

import { DEFAULT_LANGUAGE, LANGUAGE_STORAGE_KEY, SupportedLanguage } from './types';
import { safeLocalStorage } from '@shared/utils/safeStorage';

/**
 * Initialize i18next with configuration
 */
i18n
  // Load translations via HTTP
  .use(HttpBackend)
  // Detect user language
  .use(LanguageDetector)
  // Pass i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    // Fallback language
    fallbackLng: DEFAULT_LANGUAGE,

    // Supported languages
    supportedLngs: ['en', 'es', 'fr', 'de', 'pt'],

    // Strip region from detected language (es-AR → es, pt-BR → pt)
    load: 'languageOnly',

    // Debug mode (disable in production)
    debug: import.meta.env.DEV,

    // Namespaces for translation files
    ns: ['common', 'booking', 'auth', 'errors', 'profile', 'gift-cards'],
    defaultNS: 'common',

    // Backend configuration for loading translation files
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    // Detection configuration
    detection: {
      // Order of language detection
      order: ['localStorage', 'navigator'],
      // Key to store language in localStorage
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      // Cache user language
      caches: ['localStorage'],
      // Normalize detected language to short code (en-GB → en)
      convertDetectedLanguage: (lng: string) => lng.split('-')[0],
    },

    // Interpolation configuration
    interpolation: {
      // React already escapes values
      escapeValue: false,
    },

    // Preload all languages so switching is instant (no async fetch on change)
    preload: ['en', 'es', 'fr', 'de', 'pt'],

    // React-specific options
    react: {
      useSuspense: true,
    },
  });

/**
 * Change the current language
 */
export function changeLanguage(lang: SupportedLanguage): Promise<void> {
  return i18n.changeLanguage(lang).then(() => {
    safeLocalStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  });
}

/**
 * Get the current language
 */
export function getCurrentLanguage(): SupportedLanguage {
  return (i18n.language as SupportedLanguage) || DEFAULT_LANGUAGE;
}

export default i18n;
